/* params.svelte.ts generated by Svelte v5.56.5 */
import * as $ from 'svelte/internal/client';
import { PALETTE_IDS } from "../../engine/palettes";
import { DEFAULTS, SPEC_BY_KEY, sanitizeParam } from "../../engine/params";
import { newSeedString } from "../../engine/prng";
import { copyText } from "../util/clipboard";
import { presetById } from "./presets";
import { encodeHash } from "./url";

const startOnDesktop = typeof matchMedia !== "undefined" && matchMedia("(min-width: 720px)").matches;

export const debugMode = typeof location !== "undefined" && location.hash.includes("debug=1");

export const art = $.proxy({
	params: { ...DEFAULTS },
	seed: newSeedString(),
	presetId: null,
	paused: false,
	settled: false,
	uiHidden: false,
	exporting: false,
	panelOpen: startOnDesktop,
	exportMenuOpen: false,
	shuffled: false
});

export function setParam(key, value) {
	art.params[key] = value;
	art.presetId = null;
}

const LOCKS_KEY = "eigenvisuals.locks.v1";

function loadLocks() {
	try {
		const raw = JSON.parse(localStorage.getItem(LOCKS_KEY) ?? "{}");
		const out = {};

		for (const key of Object.keys(raw)) {
			if (SPEC_BY_KEY.has(key) && raw[key] === true) {
				out[key] = true;
			}
		}

		return out;
	} catch {
		return {};
	}
}

export const locks = $.proxy(loadLocks());

export function toggleLock(key) {
	locks[key] = !locks[key];

	try {
		localStorage.setItem(LOCKS_KEY, JSON.stringify(locks));
	} catch {}
}

export function resetParam(key) {
	setParam(key, DEFAULTS[key]);
}

export function applyPreset(p) {
	Object.assign(art.params, DEFAULTS, p.params);

	if (p.seed !== undefined) art.seed = p.seed;

	art.presetId = p.id;
}

export function randomize() {
	art.seed = newSeedString();
}

export function randomizeSettings() {
	const lerp = (a, b) => a + (b - a) * Math.random();
	const logLerp = (a, b) => Math.exp(lerp(Math.log(a), Math.log(b)));
	const pick = (xs) => xs[Math.floor(Math.random() * xs.length)];
	const maybe = (p, roll) => Math.random() < p ? roll() : 0;

	const rolled = {
		flowScale: logLerp(0.3, 1.6),
		octaves: pick([1, 2, 2, 3, 3, 4]),
		roughness: lerp(0.3, 0.7),
		flowStrength: lerp(0.6, 1.6),
		evolution: lerp(0.1, 0.8),
		speed: lerp(0.4, 1.1),
		windAngle: lerp(0, 360),
		windStrength: maybe(0.4, () => lerp(0.05, 0.4)),
		vortex: maybe(0.4, () => lerp(-1.2, 1.2)),
		attract: maybe(0.25, () => lerp(-0.5, 0.5)),
		particleCount: logLerp(200, 1200),
		lifetime: lerp(4, 10),
		lifetimeJitter: lerp(0.2, 0.8),
		stagger: lerp(1, 6),
		emission: pick([
			"plumes",
			"plumes",
			"plumes",
			"plumes",
			"plumes",
			"center",
			"ring",
			"line"
		]),
		plumeCount: pick([1, 1, 1, 2, 2, 3]),
		spread: logLerp(0.05, 0.18),
		halo: lerp(0.05, 0.35),
		dotSize: lerp(1, 2.6),
		sizeJitter: lerp(0.1, 0.6),
		dotOpacity: lerp(0.08, 0.3),
		glow: lerp(0.2, 0.7),
		zoom: Math.random() < 0.7 ? 1 : lerp(1, 1.8),
		exposure: lerp(0.8, 1.2),
		palette: pick(["mono", "mono", ...PALETTE_IDS]),
		symmetry: Math.random() < 0.8 ? 1 : pick([2, 3, 4, 5, 6])
	};

	for (const [key, value] of Object.entries(rolled)) {
		if (locks[key]) continue;

		const clean = sanitizeParam(key, value);

		if (clean !== undefined) art.params[key] = clean;
	}

	art.presetId = null;
	art.shuffled = true;
}

export function resetAll() {
	Object.assign(art.params, DEFAULTS);
	art.presetId = null;
}

export function applyShareState(s) {
	const preset = s.presetId ? presetById(s.presetId) : undefined;

	Object.assign(art.params, DEFAULTS, preset?.params ?? {}, s.params);
	art.presetId = preset?.id ?? null;

	if (s.seed) art.seed = s.seed; else if (preset?.seed !== undefined) art.seed = preset.seed;
}

export function shareHash() {
	const hash = encodeHash({
		seed: art.seed,
		presetId: art.presetId,
		params: $.snapshot(art.params)
	});

	return debugMode ? `${hash}&debug=1` : hash;
}

export async function copyShareLink() {
	history.replaceState(null, "", shareHash());

	return copyText(location.href);
}